import React, { useState } from 'react';
import { LEADS } from '../../constants/columns';
import MOCK_DATA from '../../MOCK_DATA.json';
import { useTable, useGlobalFilter, usePagination, useSortBy } from 'react-table';
import { leadsOptions } from '../../constants/filterOptions';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from '../../plugins/axios';
import { setFilters } from '../../utils/setFilters';
import describeFilters from '../../utils/describeFilters';
import useTableUrlSync, { useInitialTableQuery } from '../../hooks/useTableUrlState';
import { saveFilterSnapshot, clearFilterSnapshot, readFilterValues } from '../../utils/filterSnapshot';
import { sortLeadsParams } from '../../constants/sortParams';

// components
import Header from '../../components/Header/Header';
import Table from '../../components/Table/Table';
import Empty from '../../components/Empty/Empty';
import TableTabs from '../../components/TableTabs/TableTabs';
import ClientPopup from '../../components/ClientPopup/ClientPopup';
import HookForm from '../../components/HookForm/HookForm';
import EditLeadModal from '../../components/Modals/EditLeadModal';
import SelectManager from '../../components/Selects/SelectManager';
import acceptLead from '../../api/leads/acceptLead';
import alert from '../../helpers/alert';
import leadRowColor from '../../utils/leadRowColor';
import { ASSIGN_TITLE, leadPopover, withAcceptCell } from './leadsTable';
import { leadsQuery } from './leadsQuery';

// icons
import icon from '../../brand/generated/sidebar/leads.svg';
import { PERMISSION_CAN_CREATE_LEADS, ROLE_MANAGER } from '../../constants/roles';

// minWidth — чтобы на узком экране таблица скроллилась (обёртка уже с overflow-x),
// а не ужимала колонки до нечитаемого состояния. Так же сделано в Clients.
const tableStyle = { backgroundColor: 'white', minWidth: 1400 };

const formStyles = {
  record: { display: 'grid', gap: '20px', gridTemplateAreas: `'a a k k' 'b b b b' 'c c v v' 'd d d d' 'f f f f'` },
};

const FILTER_NAMES = [...leadsOptions.map((option) => option.name), 'all'];
const SNAPSHOT_KEY = 'leads';

const Leads = () => {
  const [columns, setColumns] = React.useState(LEADS);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const fromUrl = useInitialTableQuery(FILTER_NAMES);
  const [queryParams, setQueryParams] = useState(fromUrl.filters);
  const [pagination, setPagination] = useState({
    page: fromUrl.page,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
    pageSize: fromUrl.pageSize || 10,
  });
  useTableUrlSync({ queryParams, page: pagination.page, pageSize: pagination.pageSize });
  const [isFetchAll, setFetchAll] = useState(false);
  // Что сейчас отфильтровано: снимаем в момент «Найти», а не читаем потом из
  // leadsOptions — форма при закрытии панели размонтируется и значения в опциях
  // затираются, а строка над таблицей должна оставаться.
  const [appliedFilters, setAppliedFilters] = useState([]);
  // Ref на форму фильтров: снятый чип надо убрать и из запроса, и из самого селекта.
  // Перемонтировать форму, как это делает «Сбросить», тут нельзя — вместе с одним
  // значением обнулились бы и остальные.
  const filtersFormRef = React.useRef(null);
  const filtersTimer = React.useRef(null);
  const [showModal, setShowModal] = React.useState(false);
  // Рядовой менеджер лиды не раздаёт — только берёт себе кнопкой «Принять».
  const isManager = useSelector((state) => state?.user?.roleId) === ROLE_MANAGER;
  const popoverItems = React.useMemo(() => leadPopover(isManager), [isManager]);
  const acceptRef = React.useRef();

  // Лид без ответственного: в колонке «Менеджер» вместо пустоты — кнопка приёма.
  const tableColumns = React.useMemo(() => withAcceptCell(columns, () => acceptRef.current), [columns]);

  const table = useTable(
    {
      columns: tableColumns,
      data: leads,
      initialState: {
        pageSize: pagination.pageSize,
      },
      manualPagination: true,
      pageCount: pagination.totalPages,
      page: pagination.page - 1,
      disableSortRemove: true,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const { page, setPageSize } = table;

  const [value, setValue] = useState(fromUrl.filters.all || '');
  const fastSearchTimer = React.useRef(null);

  // Быстрый поиск идёт на сервер: раньше строка фильтровала только 10 уже загруженных строк страницы,
  // и лид с другой страницы «не находился».
  const onFastSearch = (text) => {
    clearTimeout(fastSearchTimer.current);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setQueryParams((prev) => ({ ...prev, all: text }));
  };

  // Ищем сами, пока печатают: Enter и «Найти» запускают поиск сразу.
  React.useEffect(() => {
    if ((value ?? '').trim() === (queryParams.all ?? '').trim()) return undefined;
    fastSearchTimer.current = setTimeout(() => onFastSearch(value), 400);
    return () => clearTimeout(fastSearchTimer.current);
  }, [value]);

  const record = [{ name: 'record', component: SelectManager, label: 'Статус записи' }];
  const [currentLead, setCurrentLead] = useState(null);

  const handleNavigate = (index) => {
    navigate(`lead/${leads.find((item) => item.id === index).id}/main-info`, {
      state: leads.find((item) => item.id === index),
    });
  };

  const addLead = () => {
    navigate('/leads/create');
  };

  const getLeads = async (params) => {
    setLoading(true);
    await axios
      .get('/api/crm/leads/getLeads', { params: leadsQuery(params) })
      .then((res) => {
        setLeads(res.data.leads);
        setPagination({
          ...res.data.pagination,
          page: res.data.pagination?.pageNumber,
          pageSize: pagination.pageSize,
        });
        setDataCount(res.data.pagination?.count);
      })
      .catch((error) => {})
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    if (!isFetchAll) {
      getLeads({ page: pagination.page, pageSize: pagination.pageSize, ...queryParams });
    }
  }, [pagination.page, pagination.pageSize, queryParams]);

  const applyFilters = (resetFilters) => {
    clearTimeout(filtersTimer.current);
    clearTimeout(fastSearchTimer.current);
    setValue('');
    setFilters(resetFilters, setFetchAll, setPageSize, setPagination, setQueryParams, leadsOptions);
    setAppliedFilters(resetFilters ? [] : describeFilters(leadsOptions));

    if (resetFilters) {
      // Дефолты обязательно: форма монтируется из них, и восстановленные из
      // адреса значения вернулись бы при следующем открытии панели.
      leadsOptions.forEach((option) => delete option.defaultValue);
      clearFilterSnapshot(SNAPSHOT_KEY);
    } else {
      saveFilterSnapshot(SNAPSHOT_KEY, leadsOptions);
    }
  };

  // Фильтры из адреса: подписи к id берём из снимка последнего применения.
  React.useEffect(() => {
    const restored = readFilterValues(SNAPSHOT_KEY, fromUrl.filters);
    leadsOptions.forEach((option) => {
      const value = restored[option.name];
      if (value === undefined) return;
      option.value = value;
      option.defaultValue = value;
      filtersFormRef.current?.setValue(option.name, value);
    });
    setAppliedFilters(describeFilters(leadsOptions));
    // Только на монтировании: дальше значениями распоряжается форма.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Фильтр применяется сам: выбрал значение — список перестроился, жать «Найти»
  // после каждого поля не нужно. Задержка одна на все поля: в тексте она гасит
  // запрос на каждую букву, в селектах просто незаметна. Значения читаем из
  // leadsOptions — HookForm держит их там в актуальном виде (item.value).
  const handleFiltersChange = React.useCallback(() => {
    clearTimeout(filtersTimer.current);
    filtersTimer.current = setTimeout(() => applyFilters(false), 400);
  }, []);

  React.useEffect(() => () => clearTimeout(filtersTimer.current), []);

  const removeFilter = (name) => {
    const option = leadsOptions.find((item) => item.name === name);
    if (option) {
      delete option.value;
      delete option.defaultValue;
    }
    // Форма может быть свёрнута — тогда её ref пустой, и чистить в ней нечего.
    filtersFormRef.current?.setValue(name, null);
    applyFilters(false);
  };

  const handleAccept = (lead) =>
    acceptLead(lead.id)
      .then(() => {
        alert.success('Лид принят в работу');
        window.dispatchEvent(new Event('crmSidebarRefresh'));
      })
      .catch(() => {})
      // И при отказе («уже взят другим») список перечитываем — он устарел.
      .finally(() => getLeads({ page: pagination.page, pageSize: pagination.pageSize, ...queryParams }));
  acceptRef.current = handleAccept;

  // Пункты меню у менеджера короче, чем у остальных, — ориентируемся по названию, не по номеру.
  const handleActionPopover = async (leadIndex, _actionIndex, title, id) => {
    setCurrentLead(leadIndex);
    if (title === 'Просмотреть лид') {
      navigate(`lead/${leads[leadIndex].id}/main-info`, { state: leads[leadIndex] });
    }
    if (title === 'Редактировать лид') {
      navigate(`lead/edit/${leads[leadIndex].id}`, { state: leads[leadIndex] });
    }
    if (title === ASSIGN_TITLE) {
      setShowModal(true);
    }
    if (title === 'Удалить лид') {
      await axios
        .get('/api/crm/leads/deleteLead', { params: { leadId: leads[leadIndex].id } })
        .then((res) => {
          getLeads({ page: pagination.page, pageSize: pagination.pageSize, ...queryParams });
        })
        .catch((error) => {});
    }
  };

  const editLead = async (val) => {
    if (record[0]?.value?.value) {
      await axios
        .post('/api/crm/leads/editLead', { managerId: record[0]?.value?.value, id: leads[currentLead].id })
        .then((res) => {
          getLeads({ page: pagination.page, pageSize: pagination.pageSize, ...queryParams });
        })
        .finally(() => {
          setShowModal(false);
        });
    }
  };

  const [showClientPopup, setShowClientPopup] = React.useState(false);
  const [clientId, setClientId] = React.useState(null);

  const sortBy = (value) => {
    if (!sortLeadsParams.get(value)) return;
    if (sortLeadsParams.get(value) === queryParams.sortBy) {
      queryParams.sortBy = '';
    } else {
      queryParams.sortBy = sortLeadsParams.get(value);
    }
    setFetchAll(false);
    setQueryParams({ ...queryParams });
  };

  return (
    <>
      {showClientPopup && <ClientPopup isOpen={showClientPopup} setOpen={setShowClientPopup} clientId={clientId} />}
      <Header title="Лиды" icon={icon} />
      <br />
      <br />
      <br />
      <br />
      <TableTabs
        setFilter={onFastSearch}
        value={value}
        setValue={setValue}
        filterComponent={
          <HookForm
            ref={filtersFormRef}
            inputs={leadsOptions}
            onValuesChange={handleFiltersChange}
            grid={{
              display: 'grid',
              gap: '10px',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
            }}
            responsiveForm
            style={{ backgroundColor: 'transparent' }}
          />
        }
        defaultFiltersOpen={Object.keys(fromUrl.filters).some((name) => name !== 'all')}
        handleSearchParams={applyFilters}
        appliedFilters={appliedFilters}
        onRemoveFilter={removeFilter}
        navigate={addLead}
        button={'Добавить лид'}
        permission={PERMISSION_CAN_CREATE_LEADS}
        inputPlaceholder={'Быстрый поиск по лидам'}
      />
      <br />
      {!page.length && !loading ? (
        <Empty />
      ) : (
        <div>
          <EditLeadModal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            title={'Назначить ответственного лид ID - ' + leads[currentLead]?.id}
            handleSubmit={editLead}
            inputs={record}
          />
          <Table
            setPageSize={(value) => {
              setPageSize(value);
            }}
            table={table}
            all={'лидов'}
            style={tableStyle}
            popover={popoverItems}
            getRowColor={leadRowColor}
            handleNavigate={handleNavigate}
            handleActionPopover={handleActionPopover}
            pagination
            sort
            setPagination={setPagination}
            setFetchAll={setFetchAll}
            isFetchAll={isFetchAll}
            setData={setLeads}
            p={pagination}
            setClientId={setClientId}
            setOpenClientPopup={setShowClientPopup}
            loading={loading}
            setLoading={setLoading}
            columns={columns}
            setColumns={setColumns}
            columnsSettingsKey="records"
            queryParams={queryParams}
            api={'/api/crm/leads/getLeads'}
            response={'leads'}
            isSortable
            dataCount={dataCount}
            handleSortTable={sortBy}
          />
        </div>
      )}
    </>
  );
};

export default Leads;
